import React from "react";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User } from "lucide-react";

export const DealFormFields = ({ form, setForm, clients, canWrite, canStage, needsApproval, editing }) => {
  const { t } = useLang();
  const cfg = useConfig();
  const set = (patch) => setForm({ ...form, ...patch });
  const stages = cfg?.activeStages || [];
  const services = cfg?.activeServices || [];

  return (
    <>
      <div className="col-span-2">
        <Label className="text-xs">{t("title")}</Label>
        <Input disabled={!canWrite} data-testid="deal-title-input" value={form.title} onChange={(e) => set({ title: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">{t("client")}</Label>
        <Select disabled={!canWrite} value={form.client_id} onValueChange={(v) => set({ client_id: v })}>
          <SelectTrigger data-testid="deal-client-select"><SelectValue placeholder={t("select_client")} /></SelectTrigger>
          <SelectContent>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company} — {c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">
          {t("stage")} {needsApproval && <span className="text-orange-600">({t("needs_approval_hint")})</span>}
        </Label>
        <Select disabled={!canStage} value={form.stage} onValueChange={(v) => set({ stage: v })}>
          <SelectTrigger data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {stages.map((s) => <SelectItem key={s.key} value={s.key}>{cfg.stageLabel(s.key)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">{t("value")} (IDR)</Label>
        <Input disabled={!canWrite} type="number" data-testid="deal-value-input" value={form.value} onChange={(e) => set({ value: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">{t("probability")} %</Label>
        <Input disabled={!canWrite} type="number" min={0} max={100} data-testid="deal-probability-input" value={form.probability} onChange={(e) => set({ probability: e.target.value })} />
      </div>
      <div className="col-span-2">
        <Label className="text-xs">{t("service")}</Label>
        <Select disabled={!canWrite} value={form.service || "none"} onValueChange={(v) => set({ service: v === "none" ? "" : v })}>
          <SelectTrigger data-testid="deal-service-select"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            {services.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">{t("expected_spk")}</Label>
        <Input disabled={!canWrite} type="date" data-testid="deal-expected-spk-input"
          value={form.expected_spk_date || ""} onChange={(e) => set({ expected_spk_date: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs">{t("expected_completion")}</Label>
        <Input disabled={!canWrite} type="date" data-testid="deal-expected-completion-input"
          value={form.expected_completion_date || ""} onChange={(e) => set({ expected_completion_date: e.target.value })} />
      </div>
      <div className="col-span-2">
        <Label className="text-xs">{t("scope")}</Label>
        <Input
          disabled={!canWrite}
          data-testid="deal-scope-input"
          placeholder={t("scope_placeholder")}
          value={form.scope || ""}
          onChange={(e) => set({ scope: e.target.value })}
        />
      </div>
      {editing && (
        <div className="col-span-2 text-[11px] text-slate-400 flex flex-wrap items-center gap-1.5" data-testid="deal-created-by">
          <User className="w-3 h-3" /> {t("pic")}: <b className="text-slate-600">{editing.pic_name || "—"}</b>
          <span>· {t("created_by")}: <b className="text-slate-600">{editing.created_by_name || "—"}</b></span>
          {editing.updated_by_name && <span>· {t("updated_by")}: <b className="text-slate-600">{editing.updated_by_name}</b></span>}
        </div>
      )}
    </>
  );
};

export default DealFormFields;
